import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
} from '@kognitos/lattice';

/** What can be done to one credential; omitted actions are hidden. */
export interface ICredentialActions {
  onRename?: () => void;
  onEditValues?: () => void;
  onDelete?: () => void;
}

/** Props for {@link CredentialActionsMenu}. */
export interface ICredentialActionsMenuProps {
  actions: ICredentialActions;
  disabled?: boolean;
}

/**
 * The ⋯ menu of a credential: rename, edit values, delete. Renders nothing
 * when no action applies. Clicks don't reach a surrounding option label.
 *
 * @param props - Component props.
 * @returns The menu, or null.
 */
export function CredentialActionsMenu({
  actions,
  disabled = false,
}: ICredentialActionsMenuProps) {
  const { t } = useTranslation('credentials');
  // An action runs once the menu has closed: the menu traps focus while open,
  // so e.g. Rename's input would otherwise lose focus at once.
  const pendingAction = useRef<(() => void) | null>(null);
  const { onRename, onEditValues, onDelete } = actions;
  if (!onRename && !onEditValues && !onDelete) return null;
  const choose = (action: () => void) => () => {
    pendingAction.current = action;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="shrink-0 text-muted-foreground"
          aria-label={t('credentials.actions.label')}
          disabled={disabled}
          // Inside a credential option (a label): don't select the option.
          onClick={event => event.stopPropagation()}
        >
          <Icon type="MoreVertical" size="sm" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        onClick={event => event.stopPropagation()}
        onCloseAutoFocus={event => {
          const action = pendingAction.current;
          if (!action) return;
          // Leave focus to the action instead of returning it to the trigger.
          event.preventDefault();
          pendingAction.current = null;
          action();
        }}
      >
        {onRename && (
          <DropdownMenuItem onClick={choose(onRename)}>
            <Icon type="PencilLine" size="sm" className="mr-2" />
            {t('credentials.actions.rename')}
          </DropdownMenuItem>
        )}
        {onEditValues && (
          <DropdownMenuItem onClick={choose(onEditValues)}>
            <Icon type="Pen" size="sm" className="mr-2" />
            {t('credentials.actions.editValues')}
          </DropdownMenuItem>
        )}
        {onDelete && (onRename || onEditValues) && <DropdownMenuSeparator />}
        {onDelete && (
          <DropdownMenuItem
            onClick={choose(onDelete)}
            className="text-destructive focus:text-destructive"
          >
            <Icon type="Trash" size="sm" className="mr-2" />
            {t('credentials.actions.delete')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
